import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  getDoc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "../../../firebase";
import ViewMessages from "./ViewMessages";

// Dayjs config
import dayjs from "dayjs";
import calendar from "dayjs/plugin/calendar";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
import "dayjs/locale/es";

dayjs.extend(calendar);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.locale("es");

dayjs.updateLocale("es", {
  calendar: {
    sameDay: "[Hoy a las] h:mm A",
    nextDay: "[Mañana a las] h:mm A",
    nextWeek: "dddd [a las] h:mm A",
    lastDay: "[Ayer a las] h:mm A",
    lastWeek: "[El] dddd [pasado a las] h:mm A",
    sameElse: "D [de] MMM [a las] h:mm A",
  },
});

const MessagesPromotionalContainer = ({ promoProfileId, onOpenAdmin }) => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [loadingUser, setLoadingUser] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!promoProfileId || !auth.currentUser) return;

    const q = query(
      collection(db, "chatsPromotional"),
      where("users", "array-contains", promoProfileId),
      orderBy("lastMessageTimestamp", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        try {
          const chatsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          const otherUserIds = [
            ...new Set(
              chatsData
                .map((chat) => chat.users.find((uid) => uid !== promoProfileId))
                .filter(Boolean)
            ),
          ];

          const usersInfo = {};
          await Promise.all(
            otherUserIds.map(async (uid) => {
              const userSnap = await getDoc(doc(db, "users", uid));
              if (userSnap.exists()) {
                usersInfo[uid] = { uid, isPromo: false, ...userSnap.data() };
              } else {
                // Try fetching from promoProfiles if user not found
                const usersSnap = await getDocs(collection(db, "users"));
                for (const userDoc of usersSnap.docs) {
                  const promoProfileRef = doc(
                    db,
                    "users",
                    userDoc.id,
                    "promoProfiles",
                    uid
                  );
                  const promoSnap = await getDoc(promoProfileRef);
                  if (promoSnap.exists()) {
                    usersInfo[uid] = { uid, isPromo: true, ...promoSnap.data() };
                    break;
                  }
                }
              }
            })
          );

          setUserDetails(usersInfo);
          setChats(chatsData);

          if (!selectedChat && chatsData.length > 0) {
            setSelectedChat(chatsData[0]);
          }

          if (selectedChat && !chatsData.find((c) => c.id === selectedChat.id)) {
            setSelectedChat(null);
          }

          setError(null);
        } catch (error) {
          console.error("Error fetching chats:", error);
          setError("Error al cargar los chats. Inténtalo de nuevo.");
        }
      },
      (error) => {
        console.error("Error in onSnapshot:", error);
        setError("Error al cargar los chats. Inténtalo de nuevo.");
      }
    );

    return () => unsubscribe();
  }, [promoProfileId, selectedChat]);

  const handleChatClick = async (chat) => {
    setLoadingUser(true);
    try {
      const otherUserId = chat.users.find((uid) => uid !== promoProfileId);
      if (!otherUserId) {
        setError("No se encontró el otro usuario en el chat.");
        return;
      }
      setSelectedChat(chat);

      // Mark chat as read
      await updateDoc(doc(db, "chatsPromotional", chat.id), {
        [`readStatus.${promoProfileId}`]: true,
      });

      setError(null);
    } catch (error) {
      console.error("Error selecting chat:", error);
      setError("Error al seleccionar el chat. Inténtalo de nuevo.");
    } finally {
      setLoadingUser(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp?.toDate) return "";
    return dayjs(timestamp.toDate()).calendar();
  };

  if (!auth.currentUser) {
    return <p className="text-red-500">Debes iniciar sesión para ver los mensajes.</p>;
  }

  if (!promoProfileId) {
    return <p className="text-gray-600">Selecciona un perfil promocional para ver los mensajes.</p>;
  }

  if (chats.length === 0) {
    return <p className="text-gray-600">No tienes chats activos para este perfil.</p>;
  }

  return (
    <div className="flex flex-col md:flex-row h-full max-h-screen border rounded-lg shadow-lg overflow-hidden bg-white dark:bg-gray-900 transition-colors">
      {/* Sidebar */}
      <aside className="w-full md:w-80 border-r dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-y-auto max-h-screen">
        <h3 className="font-bold text-lg p-4 border-b dark:border-gray-700 text-gray-800 dark:text-white">
          Chats Promocionales
        </h3>

        {error && <p className="text-red-500 px-4">{error}</p>}

        <ul>
          {chats.map((chat) => {
            const otherUserUid = chat.users.find((uid) => uid !== promoProfileId);
            const otherUser = userDetails[otherUserUid];
            const isSelected = selectedChat?.id === chat.id;
            const isUnread = !chat.readStatus?.[promoProfileId];

            return (
              <li
                key={chat.id}
                onClick={() => handleChatClick(chat)}
                className={`cursor-pointer px-4 py-3 flex items-start gap-3 border-b dark:border-gray-700 transition-colors ${
                  isSelected
                    ? "bg-indigo-600 text-white"
                    : "hover:bg-indigo-100 dark:hover:bg-indigo-700 text-gray-900 dark:text-gray-200"
                }`}
              >
                {otherUser?.photoURL || otherUser?.profilePhotoUrl ? (
                  <img
                    src={otherUser.photoURL || otherUser.profilePhotoUrl}
                    alt="avatar"
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {otherUser?.nickname?.[0]?.toUpperCase() ||
                      otherUser?.name?.[0]?.toUpperCase() ||
                      otherUser?.nombre?.[0]?.toUpperCase() ||
                      "U"}
                  </div>
                )}

                <div className="flex-1 min-w-0 overflow-hidden">
                  <p className="font-semibold truncate">
                    {otherUser?.nickname ||
                      otherUser?.name ||
                      otherUser?.nombre ||
                      `Usuario: ${otherUserUid}`}
                    {otherUser?.isPromo && (
                      <span className="text-xs text-blue-300 ml-2">(Promocional)</span>
                    )}
                  </p>

                  <p className="text-sm truncate opacity-80">
                    {chat.lastMessage || "Sin mensajes aún"}
                  </p>

                  {chat.lastMessageTimestamp && (
                    <small className="block text-xs mt-1 opacity-60 truncate">
                      {formatTimestamp(chat.lastMessageTimestamp)}
                    </small>
                  )}

                  {isUnread && (
                    <span className="text-blue-400 text-xs truncate">Nuevo</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-4 flex flex-col bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100">
        {loadingUser && (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Cargando chat...
          </p>
        )}
        {selectedChat ? (
          <>
            <div className="mb-4 border-b pb-2 border-gray-300 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                Hablando con:{" "}
                {
                  userDetails[selectedChat.users.find((uid) => uid !== promoProfileId)]
                    ?.nickname ||
                    userDetails[selectedChat.users.find((uid) => uid !== promoProfileId)]
                      ?.name ||
                    userDetails[selectedChat.users.find((uid) => uid !== promoProfileId)]
                      ?.nombre ||
                    "Usuario"
                }
                {userDetails[
                  selectedChat.users.find((uid) => uid !== promoProfileId)
                ]?.isPromo && (
                  <span className="text-xs text-blue-500">(Promocional)</span>
                )}
              </h2>
              
            </div>
            <ViewMessages
              promoProfileUid={promoProfileId}
              chatId={selectedChat.id}
              otherUserUid={selectedChat.users.find((uid) => uid !== promoProfileId)}
              otherUserData={userDetails[selectedChat.users.find((uid) => uid !== promoProfileId)]}
            />
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500 dark:text-gray-400">
              Selecciona un chat para empezar a conversar.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default MessagesPromotionalContainer;